<template>
  <div class="data-product">
    <v-row class="ma-0">
      <v-col cols="12" md="6" class="data-product__left px-0">
        <div class="px-4 px-md-6">
          <template v-if="$vuetify.breakpoint.mdAndUp">
            <div class="my-6 data-product__header">
              <slot name="meta">
                <ProductMeta
                  v-if="
                    settings.lastUpdated || settings.productionTime || (settings.sources && settings.sources.length > 0)
                  "
                  class="d-inline-block mb-6"
                  :last-updated="settings.lastUpdated"
                  :production-time="settings.productionTime"
                  :sources="settings.sources"
                ></ProductMeta>
              </slot>
              <slot name="description">
                <div class="data-product__description" v-if="settings.description" v-html="settings.description"></div>
              </slot>
              <slot name="tags">
                <ProductTags v-if="productTags.length > 0" :items="productTags" class="my-6" />
              </slot>
            </div>
          </template>
          <div class="data-product__form" ref="productForm">
            <slot name="form">
              <!-- eslint-disable vue/no-mutating-props -->
              <ng-fieldset
                v-if="settings.productOptions && settings.productOptions.formFields && productModel.formModel"
                :fields="settings.productOptions.formFields"
                v-model="productModel.formModel"
              />
              <!-- eslint-enable -->
            </slot>
          </div>
          <div class="data-product__links pb-6">
            <slot name="links">
              <ProductLinks
                v-if="settings.samplesUrl || dataStructureUrl"
                :samples-url="settings.samplesUrl"
                :data-structure-url="dataStructureUrl"
              />
            </slot>
          </div>
          <slot
            name="layers"
            :layers="layers"
            :layers-model="layersModel"
            :update-layers="updateLayers"
            :layers-status="layersStatus"
          >
            <LayerList
              v-if="layers"
              :items="layers"
              :value="layersModel"
              @input="updateLayers"
              :status="layersStatus"
              :title="$t('product.layers')"
              v-bind="settings.layerList && settings.layerList.attrs"
            />
          </slot>
        </div>
        <!-- disabled -->

        <div class="data-product__demo">
          <slot name="demo">
            <div v-if="mapLink || (settings.images && settings.images.length > 0)">
              <v-container fluid>
                <h2>{{ $t('product.demoTitle') }}</h2>
                <p style="max-width: 730px" v-html="$t('product.demoDescription')"></p>
              </v-container>
              <product-gallery
                v-if="isReady"
                :images="settings.images"
                :map-link="mapLink"
                :map-image="addTimestampToUrl(settings.demoMapImage)"
              ></product-gallery>
            </div>
          </slot>
        </div>

        <div class="data-product__about mt-3 mb-10">
          <v-container fluid>
            <v-row>
              <v-col>
                <h2>{{ $t('product.aboutTitle') }}</h2>
                <slot name="about">
                  <p v-html="$t(`product.${id}.about.p1`)"></p>
                  <p v-html="$t(`product.${id}.about.p2`)"></p>
                </slot>
                <p v-html="$t('product.aboutQuestions')"></p>
              </v-col>
            </v-row>
          </v-container>
        </div>
        <ProductSummary
          class="data-product__summary mt-4"
          :is-loading="!isReady"
          :errors="summaryErrors"
          @submit="submitOrder"
        />
        <checkout-dialog
          v-if="price"
          v-model="checkoutDialog"
          @success:invoice="invoiceSuccessDialog = true"
          @success:freeExtraction="isPaymentSucceeded = true"
        />
        <invoice-success-dialog v-model="invoiceSuccessDialog"></invoice-success-dialog>
        <payment-success-dialog v-model="isPaymentSucceeded" :production-time="settings.productionTime" />
      </v-col>
      <v-col cols="12" md="6" order="first" order-md="last" class="data-product__right px-4 px-md-6 py-0">
        <template v-if="$vuetify.breakpoint.smAndDown">
          <div class="my-6 data-product__description">
            <slot name="meta">
              <ProductMeta
                v-if="
                  settings.lastUpdated || settings.productionTime || (settings.sources && settings.sources.length > 0)
                "
                class="d-inline-block mb-6"
                :last-updated="settings.lastUpdated"
                :production-time="settings.productionTime"
                :sources="settings.sources"
              ></ProductMeta>
            </slot>
            <slot name="description">
              <div v-if="settings.description" v-html="settings.description"></div>
            </slot>
          </div>
        </template>

        <div class="data-product__map-wrapper py-4 py-md-6">
          <slot name="map">
            <component
              v-if="!!currentRegion"
              :is="mapComponent"
              key="map"
              class="data-product-map"
              ref="map"
              basemapUrl="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              :isLoading="!isCustom && !currentRegionGeometry"
              :bounds="currentRegion && currentRegion.boundary"
              :value="
                currentRegionGeometry &&
                currentRegionGeometry.geojson && {
                  geometry: currentRegionGeometry.geojson,
                  meta: regionData.customRegionMeta,
                }
              "
              v-bind="mapOptions"
              @input="updateGeometry"
              @update:zoom="updateZoom"
            >
              <template v-slot:prepend>
                <slot name="map-prepend" :zoom="mapZoom"></slot>
              </template>
              <template v-slot:append>
                <l-control position="bottomleft" v-if="isDrawYourBorderShown">
                  <ng-button color="light" icon-name="mdi-open-in-new" icon-position="right" @click="goToCustom">{{
                    $t('product.drawYourBorder')
                  }}</ng-button>
                </l-control>
                <slot name="map-append" :zoom="mapZoom"></slot>
              </template>
            </component>
          </slot>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import { LControl } from 'vue2-leaflet';
import { isInViewportY, scrollTo } from '@ngservices_front/js/viewport';
import productService from '@/services/product/productService';
import paymentService from '@/services/payment/paymentService';
import gtagService from '@/services/loggers/gtagService';
import ProductSummary from '@/components/product/ProductSummary.vue';
import CheckoutDialog from '@/components/product/CheckoutDialog.vue';
import InvoiceSuccessDialog from '@/components/product/InvoiceSuccessDialog.vue';
import PaymentSuccessDialog from '@/components/payment/PaymentSuccessDialog.vue';
import AppMap from '@/components/map/AppMap.vue';
import CustomRegionMap from '@/components/product/CustomRegionMap.vue';
import ProductLinks from '@/components/product/ProductLinks.vue';
import ProductGallery from '@/components/product/ProductGallery.vue';
import LayerList from '@/components/product/layers/LayerList.vue';
import ProductMeta from '@/components/product/ProductMeta.vue';
import ProductTags from '@/components/product/ProductTags.vue';
import { LAST_UPDATED_TODAY, ERRORS_TO_SAVE_TO_STORE } from '@/constants/product/productConstants';
import { addTimestampToUrl } from '@/utils/urlUtils';
import { getProductCanonicalLink, getProductAlternateLinks } from '@/utils/vueMetaUtils';
import { isCustomRegion, isRegionFromCountry } from '@/utils/regionUtils';
import { PAYMENT_SUCCESS_QUERY } from '@/constants/payment/paymentConstants';
import regionService from '@/services/regionService';
import { getProductNameFromRoute, getProductRoute } from '@/utils/productRouterUtils';
import * as promotionService from '@/services/promotionService';

export default {
  name: 'DataProduct',
  components: {
    ProductSummary,
    CheckoutDialog,
    InvoiceSuccessDialog,
    PaymentSuccessDialog,
    AppMap,
    CustomRegionMap,
    ProductLinks,
    ProductGallery,
    LayerList,
    LControl,
    ProductMeta,
    ProductTags,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    productModel: {
      type: Object,
      required: true,
    },
    settings: {
      type: Object,
      default: () => {},
    },
    isProductDataReady: {
      type: Boolean,
      default: true,
    },
    isFormValid: {
      type: Boolean,
      default: true,
    },
    mapOptions: {
      type: Object,
      default: () => {},
    },
  },
  metaInfo() {
    return {
      title: !!this.currentRegion && this.$route.meta.getTitle(this.$route, this.id),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.$route.meta.getDescription(this.$route, this.id),
        },
      ],
      link: [
        getProductCanonicalLink(this.$route, this.id),
        ...getProductAlternateLinks(this.$route, this.id, { onlyForCanonical: true }),
      ],
    };
  },
  data() {
    return {
      checkoutDialog: false,
      invoiceSuccessDialog: false,
      isPaymentSucceeded: false,
      layers: [],
      layersModel: [],
      isLayersLoadingCompleted: false,
      layersStatus: 'iddle',
      geomLocal: undefined,
      mapZoom: undefined,
      LAST_UPDATED_TODAY,
      PAYMENT_SUCCESS_QUERY,
    };
  },
  computed: {
    ...mapState('regions', ['currentRegion', 'currentRegionCode', 'currentRegionGeometry']),
    ...mapState('app', ['apiErrors']),
    ...mapState('map', ['mapWarnings']),
    ...mapState('product', ['price']),
    ...mapState('promotions', ['productCoupon', 'productCouponStatus', 'productCouponError']),
    ...mapGetters('product', ['productDataFormatted']),
    productDataValidation() {
      if (this.productDataFormatted) {
        return productService.validateProductData(this.productDataFormatted);
      }
      return null;
    },
    summaryErrors() {
      const apiErrorMessages = [...Object.entries(this.apiErrors).map((x) => x[1])];
      const validationErrors = this.productDataValidation ? this.productDataValidation.errors : [];
      return [...apiErrorMessages, ...validationErrors, ...this.mapWarnings];
    },
    isReady() {
      return !!this.regionData && this.isProductDataReady && this.isLayersLoadingCompleted;
    },
    regionData() {
      return this.currentRegion && { ...this.currentRegion };
    },
    isCustom() {
      return isCustomRegion(this.currentRegionCode);
    },
    mapComponent() {
      return this.currentRegionCode && this.isCustom ? CustomRegionMap : AppMap;
    },
    productTags() {
      return (this.settings.tags && this.settings.tags.split(',')) || [];
    },
    isDrawYourBorderShown() {
      const isProductAvailableInCustom =
        this.settings.isWorldwide || (isRegionFromCountry(this.currentRegionCode, 'ru') && this.$i18n.locale === 'ru');
      return this.currentRegionCode && !this.isCustom && isProductAvailableInCustom;
    },
  },
  watch: {
    id: {
      handler(val) {
        this.reset();
        this.CLEAR_API_ERRORS();
        this.SET_PRODUCT_ID(val);
        this.SET_PRODUCT_SERVER_ID(this.settings.serverId);
      },
      immediate: true,
    },
    isReady: {
      handler(val) {
        if (val) {
          if (this.$refs.map) this.$refs.map.redraw();
          this.$emit('ready');
        }
      },
      immediate: true,
    },
    productModel: {
      handler(val) {
        this.SET_PRODUCT_OPTIONS({ ...val });
      },
      deep: true,
      immediate: true,
    },
    layersModel: {
      handler(newLayers) {
        this.SET_PRODUCT_LAYERS(newLayers);
      },
      immediate: true,
    },
    currentRegionCode: {
      handler(val) {
        if (val) {
          this.reset();
          this.initLayers();
        }
      },
    },
    '$i18n.locale': {
      handler() {
        this.initLayers();
      },
    },
    'currentRegionGeometry.wkt': {
      handler() {
        this.REMOVE_API_ERRORS(ERRORS_TO_SAVE_TO_STORE);
      },
    },
    productDataFormatted: {
      async handler(newProductData) {
        if (this.isReady)
          productService.manageProductPriceUpdate({
            productOptions: { ...newProductData, coupon: this.$route.query?.promo },
          });
      },
      immediate: true,
      deep: true,
    },
    price(newPrice) {
      if (newPrice)
        gtagService.fireEcommerceEvent({
          eventName: 'view_item',
          eventData: {
            value: newPrice.amount,
            currency: newPrice.currency.toUpperCase(),
            items: [
              gtagService.createEcommerceItem({
                productItem: this.productDataFormatted,
                price: newPrice,
              }),
            ],
          },
        });
    },
    productCouponStatus() {
      if (this.productCouponStatus === 'failed' && this.productCouponError !== this.$t('order.itemLimitExceeded')) {
        promotionService.disableCouponActivationFromRoute({ router: this.$router, currentRoute: this.$route });
      }
    },
  },
  created() {
    const fallbackLocale = process.env.VUE_APP_I18N_FALLBACK_LOCALE;
    const { locale } = this.$i18n;
    this.checkSuccededPayment();
    this.dataStructureUrl =
      this.settings.dataStructureUrl &&
      (this.settings.dataStructureUrl[locale] || this.settings.dataStructureUrl[fallbackLocale]);
    this.mapLink =
      this.settings.demoMapUrl && (this.settings.demoMapUrl[locale] || this.settings.demoMapUrl[fallbackLocale]);
    if (this.currentRegionCode) this.initLayers();
  },
  mounted() {
    this.SET_PRODUCT_URL(this.$route.fullPath);
  },
  methods: {
    ...mapMutations({
      SET_PRODUCT_OPTIONS: 'product/SET_PRODUCT_OPTIONS',
      SET_DEFAULT_LAYERS: 'product/SET_DEFAULT_LAYERS',
      SET_PRODUCT_LAYERS: 'product/SET_PRODUCT_LAYERS',
      SET_PRODUCT_ID: 'product/SET_PRODUCT_ID',
      SET_PRODUCT_SERVER_ID: 'product/SET_PRODUCT_SERVER_ID',
      SET_PRODUCT_URL: 'product/SET_PRODUCT_URL',
      SET_PRICE: 'product/SET_PRICE',
      REMOVE_API_ERRORS: 'app/REMOVE_API_ERRORS',
      CLEAR_API_ERRORS: 'app/CLEAR_API_ERRORS',
    }),
    addTimestampToUrl,
    checkSuccededPayment() {
      this.isPaymentSucceeded = paymentService.isPaymentSucceeded(this.$route.query);
      if (this.isPaymentSucceeded) {
        const newQuery = { ...this.$route.query };
        delete newQuery[this.PAYMENT_SUCCESS_QUERY];
        this.$router.replace({
          ...this.$router.currentRoute,
          query: newQuery,
        });
      }
    },
    submitOrder() {
      this.$emit('orderSubmit');
      if (this.isFormValid) {
        this.checkoutDialog = true;
        gtagService.fireEcommerceEvent({
          eventName: 'add_to_cart',
          eventData: {
            value: this.price.amount,
            currency: this.price.currency.toUpperCase(),
            items: [
              gtagService.createEcommerceItem({
                productItem: this.productDataFormatted,
                price: this.price,
              }),
            ],
          },
        });
      } else {
        const { productForm: productFormNode } = this.$refs;
        const fixedHeaderHeight = 100;
        if (!isInViewportY(productFormNode, { offset: { top: fixedHeaderHeight, bottom: 0 } }))
          scrollTo(productFormNode, { offset: fixedHeaderHeight });
      }
    },
    async initLayers() {
      try {
        this.layersStatus = 'loading';
        const layers = await productService.getProductLayers({
          id: this.settings.serverId,
          regionCode: this.currentRegionCode,
        });
        this.layers = layers;
        this.layersModel = layers.map((x) => x.id);
        this.SET_DEFAULT_LAYERS(this.layersModel);
        this.layersStatus = 'success';
      } catch (e) {
        this.layersStatus = 'failure';
      } finally {
        this.isLayersLoadingCompleted = true;
      }
    },
    updateLayers(newLayers) {
      this.layersModel = newLayers;
    },
    reset() {
      this.layersStatus = 'iddle';
      productService.resetProductData();
    },
    updateGeometry(geometryObject) {
      regionService.updateRegionGeometry(geometryObject);
    },
    updateZoom(newZoom) {
      this.mapZoom = newZoom;
    },
    goToCustom() {
      if (window.ym) window.ym(42050389, 'reachGoal', 'drawYourBorder');
      const customRoute = getProductRoute({
        productSettings: this.settings,
        regionCode: 'custom',
        productName: getProductNameFromRoute(this.$route),
        queryParams: { ...this.$route.query, bounds: JSON.stringify(this.currentRegion.boundary) },
      });
      this.$router.push(customRoute);
    },
  },
};
</script>

<style lang="scss" scoped>
$product-right-max-height: calc(100vh - #{$product-tabs-height} - #{$region-title-height} - #{$header-height});

.data-product {
  &__left {
    @media (min-width: map-get($grid-breakpoints, 'md')) {
      border-right: 1px solid $border-color;
    }
  }

  &__right {
    position: sticky;
    top: $product-tabs-height;
    max-height: $product-right-max-height;

    @media (max-width: map-get($grid-breakpoints, 'md')) {
      position: relative;
      top: 0;
      max-height: none;
    }
  }

  &__option-group {
    padding-top: 16px;
    margin-top: 16px;
    border-top: 1px solid $border-color;
  }
  &__summary {
    position: sticky;
    bottom: 0;
    z-index: 5;
  }
  &__form,
  /* &__description, */
  &__links {
    max-width: 480px;
  }
  //&__demo,
  &__about {
    max-width: 1460px;
  }
  &__last-updated-chip {
    height: 26px;
  }

  &__meta {
    border: 1px solid $border-color;
  }
}

.with-top-banner {
  .data-product__right {
    max-height: calc(#{$product-right-max-height} - #{$top-banner-height});
    @media (max-width: map-get($grid-breakpoints, 'md')) {
      max-height: none;
    }
  }
}
.data-product-map {
  @media (max-width: map-get($grid-breakpoints, 'md')) {
    height: 300px;
  }
}

.data-product__map-wrapper {
  height: 100%;
  transition: max-height 0.2s;

  &.fullHeight {
    max-height: 100vh !important;
  }

  @media (max-width: map-get($grid-breakpoints, 'md')) {
    height: auto;
  }
}
</style>
